import React, { useContext, useEffect, useState } from "react";
import { Avatar, Box, Typography, Button, ButtonBase, Card } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ordersPathTo } from '../utils/Api';
import OrderCard from "./OrderCard";
import { UserContext } from "../contexts/UserContext";
import { ShoppingCartProductsContext } from "../contexts/ShoppingCartProductsContext";
import { currency, productUnitPrice } from "./building_blocks/Currency";
import { apiBaseUrl } from "../utils/Api/Api";

/* Icons */
import { ArrowBackRounded } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { layoutCardRadius, layoutIconButtonRadius } from '../theme/colors';

/* Padrão Campos Endereço */
const FormStatus = ({ label, children, sx = {} }) => (

  /* Info Endereço */
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch', ...sx }}>

    {/* Título Campo */}
    <Typography sx={{ color: '#000', fontFamily: 'Nunito', fontSize: '16px', fontWeight: 700, lineHeight: '20px' }}>
      {label}
    </Typography>

    {/* TextField // Autocomplete*/}
    {children}
  </Box>
);

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser } = useContext(UserContext);
  const { shoppingCartProducts } = useContext(ShoppingCartProductsContext);
  const navigate = useNavigate();
  const defaultAddress = currentUser?.default_address;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const result = await ordersPathTo.list({ with_logo: true, with_items: true });
        setOrders(result.data.orders || []);
        setIsLoading(false);
      } catch (error) {
        console.error('Erro ao carregar pedidos:', error);
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const formatAddress = (address) => {
    if (!address) return 'Adicionar endereço de entrega';

    return [
      [address.logradouro, address.numero].filter(Boolean).join(', ')
    ].filter(Boolean).join(', ');
  };

  const activeStatuses = ['open', 'pending', 'paid', 'accepted', 'courier', 'delivered'];
  const activeOrders = orders.filter((order) => activeStatuses.includes(order.status));
  const latestFinalizedOrder = orders.find((order) => (
    order.status === 'done' &&
    order.provider?.published === true
  )) || null;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (latestFinalizedOrder) {
      localStorage.setItem('latestOrder', JSON.stringify(latestFinalizedOrder));
      localStorage.setItem('latestOrders', JSON.stringify([latestFinalizedOrder]));
    } else {
      localStorage.removeItem('latestOrder');
      localStorage.removeItem('latestOrders');
    }
  }, [isLoading, latestFinalizedOrder]);

  const renderEmptyMessage = (message) => (
    <Card sx={{ display: 'flex', padding: '24px', alignItems: 'center', alignSelf: 'stretch', bgcolor: 'surface.card', borderRadius: layoutCardRadius }}>
      <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', fontWeight: 400, lineHeight: '24px', color: '#546E7A' }}>
        {message}
      </Typography>
    </Card>
  );

  const renderOrderList = (orderList, emptyMessage) => {
    if (isLoading) {
      return renderEmptyMessage('Carregando...');
    }

    if (orderList.length === 0) {
      return renderEmptyMessage(emptyMessage);
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignSelf: 'stretch', width: '100%' }}>
        {orderList.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            handleCardClick={() => navigate('/orders/' + order.id)}
          />
        ))}
      </Box>
    );
  };

  const renderCartProducts = () => {
    if (!shoppingCartProducts || shoppingCartProducts.length === 0) {
      return renderEmptyMessage('Seu carrinho está vazio');
    }

    const shoppingProvider = JSON.parse(localStorage.getItem('shoppingProvider') || '{}');
    const cartTotal = shoppingCartProducts.reduce(
      (total, product) => total + productUnitPrice(product) * product.cartQuantity,
      0
    );
    const cartQuantity = shoppingCartProducts.reduce(
      (total, product) => total + product.cartQuantity,
      0
    );

    return (
      <Card
        sx={{ display: 'flex', padding: '24px', alignItems: 'flex-start', gap: 2, alignSelf: 'stretch', bgcolor: 'surface.card', borderRadius: layoutCardRadius }}
      >
        <Avatar
          src={shoppingProvider.business_logo && `${apiBaseUrl}${shoppingProvider.business_logo}`}
          sx={{ width: '40px', height: '40px', bgcolor: '#CFD8DC' }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 0.5, flexGrow: '1 0 0', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
            <Typography variant="storeName" sx={{ color: '#263238' }}>
              {shoppingProvider.name || 'Carrinho'}
            </Typography>
            <ButtonBase onClick={() => navigate('/shopping-cart')}
              sx={{display: 'flex', p: 1, gap: '10px', alignItems: 'center', borderRadius: '50%', bgcolor: 'surface.muted', color: 'text.secondary', cursor: 'pointer',
                '&:hover': {
                  bgcolor: '#CFD8DC',
                  color: '#263238',
                }
              }}>
              <ShoppingCartIcon sx={{ width: '18px', height: '18px' }} />
            </ButtonBase>
          </Box>

          <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', fontWeight: 400, lineHeight: '21px', color: '#546E7A' }}>
            {currency(cartTotal)} ({cartQuantity} {cartQuantity === 1 ? 'item' : 'itens'})
          </Typography>
        </Box>
      </Card>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 3 }}>
      {/* Voltar e Título */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, alignSelf: 'stretch', position: 'relative' }}>
        <Button data-no-shadow="true" onClick={() => window.history.back()} sx={{ color: 'text.primary', borderRadius: layoutIconButtonRadius }}>
          <ArrowBackRounded />
        </Button>

        {/* Título */}
        <Typography variant="pageTitle" sx={{ width: 'auto', transform: 'translateX(-50%)', position: 'absolute', left: '50%' }}>Pedidos</Typography>
      </Box>

      {/* Endereço de Entrega */}
      <Card
        sx={{ display: 'flex', padding: '24px', flexDirection: 'column', alignItems: 'flex-start', gap: '7px', alignSelf: 'stretch', bgcolor: 'surface.card', borderRadius: layoutCardRadius, textAlign: 'left' }}
      >
        <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', fontWeight: 400, lineHeight: '24px' }}>
          Entregar em:
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, alignSelf: 'stretch' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon sx={{ color: '#F8963E' }} />
            <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', fontWeight: 600, lineHeight: '20px', color: '#263238' }}>
              {formatAddress(defaultAddress)}
            </Typography>
          </Box>

          <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, lineHeight: '20px', color: '#F8963E', cursor: 'pointer' }}>
            {/* Botão para Mudar Img */}
            <ButtonBase
              onClick={() => navigate('/address?from=orders')}
              variant="outlined"
              component="label"
              sx={{ width: '32px', height: '32px', borderRadius: '50%', bgcolor: '#263238', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EditIcon sx={{ width: '16px', height: '16px' }} />
            </ButtonBase>
          </Typography>
        </Box>
      </Card>

      <Box sx={{ alignSelf: 'stretch' }}>
        <FormStatus label="Ativos">
          {renderOrderList(activeOrders, 'Você não tem pedidos ativos')}
        </FormStatus>
      </Box>

      <FormStatus label="Carrinho">
        {renderCartProducts()}
      </FormStatus>

      <FormStatus label="Antigos">
        {renderOrderList(latestFinalizedOrder ? [latestFinalizedOrder] : [], 'Você ainda não tem pedidos finalizados')}
      </FormStatus>
    </Box>
  );
};

export default MyOrders;
